import React, { useState, useEffect } from 'react';
import {
  X, Monitor, Terminal, Apple, Download, CheckCircle,
  Copy, ChevronRight, AlertTriangle, Shield, Zap, Loader2, ExternalLink
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || ''}/api/v1`;

const getAuthToken = () => localStorage.getItem('access_token');

/** Download a file using direct URL with token (avoids fetch/HTTP2 issues). */
const downloadFile = (url, fallbackFilename) => {
  const token = getAuthToken();
  if (!token) {
    toast.error('Vous devez être connecté pour télécharger');
    return false;
  }
  const sep = url.includes('?') ? '&' : '?';
  const dlUrl = `${url}${sep}_token=${encodeURIComponent(token)}`;
  window.location.href = dlUrl;
  return true;
};

const copyToClipboard = (text) =>
  navigator.clipboard.writeText(text).then(() => toast.success('Copié !'));

const CodeBlock = ({ code }) => (
  <div className="relative bg-gray-900 rounded-lg p-3 mt-2 font-mono text-xs text-green-300 border border-gray-700 overflow-x-auto">
    <button
      onClick={() => copyToClipboard(code)}
      className="absolute top-2 right-2 p-1 text-gray-500 hover:text-gray-300 transition-colors"
      title="Copier"
    >
      <Copy className="w-3.5 h-3.5" />
    </button>
    <pre className="pr-6 whitespace-pre-wrap break-all">{code}</pre>
  </div>
);

const TABS = [
  { id: 'windows', label: 'Windows', icon: Monitor },
  { id: 'linux',   label: 'Linux',   icon: Terminal },
  { id: 'macos',   label: 'macOS',   icon: Apple },
];

// How long (seconds) before showing the manual fallback command
const FALLBACK_DELAY = 30;
// Poll interval in ms
const POLL_INTERVAL = 5000;

export default function DownloadAgentModal({ isOpen, onClose, onRegister, workspaceId }) {
  const [activeTab, setActiveTab] = useState('windows');
  const [winDone, setWinDone] = useState(false);

  // Connection tracking state
  const [connected, setConnected] = useState(null);   // agent object when online
  const [waitSecs, setWaitSecs]   = useState(0);
  const [showFallback, setShowFallback] = useState(false);
  const [downloadTime, setDownloadTime] = useState(null);

  if (!isOpen) return null;

  const serverUrl = window.location.origin;

  /** Windows: create agent + download pre-configured zip in one click */
  const handleWindowsDownload = () => {
    setWinDone(false);
    setConnected(null);
    setWaitSecs(0);
    setShowFallback(false);
    const params = new URLSearchParams();
    if (workspaceId && typeof workspaceId === 'string') params.set('workspace_id', workspaceId);
    const url = `${API_BASE_URL}/agents/installer/windows${params.size ? '?' + params : ''}`;
    downloadFile(url, 'SYLinkAgent.zip');
    setDownloadTime(Date.now());
    setWinDone(true);
    toast.success('Téléchargement démarré — extrayez le ZIP et double-cliquez sur INSTALL.cmd');
  };

  return (
    <ModalInner
      activeTab={activeTab} setActiveTab={setActiveTab}
      winDone={winDone} connected={connected} setConnected={setConnected}
      waitSecs={waitSecs} setWaitSecs={setWaitSecs}
      showFallback={showFallback} setShowFallback={setShowFallback}
      downloadTime={downloadTime}
      serverUrl={serverUrl}
      handleWindowsDownload={handleWindowsDownload}
      onClose={onClose} onRegister={onRegister}
    />
  );
}

// Inner component so hooks work with early-return pattern
function ModalInner({
  activeTab, setActiveTab,
  winDone, connected, setConnected,
  waitSecs, setWaitSecs,
  showFallback, setShowFallback,
  downloadTime,
  serverUrl,
  handleWindowsDownload,
  onClose, onRegister,
}) {
  // Poll for newly connected agent after download
  useEffect(() => {
    if (!winDone || connected || !downloadTime) return;

    let elapsed = 0;
    const timer = setInterval(async () => {
      elapsed += POLL_INTERVAL / 1000;
      setWaitSecs(elapsed);
      if (elapsed >= FALLBACK_DELAY) setShowFallback(true);

      try {
        const token = getAuthToken();
        const res = await fetch(`${API_BASE_URL}/agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const agents = await res.json();
          // Agent registered within the last 10 minutes and now online
          const TEN_MIN = 10 * 60 * 1000;
          const fresh = agents.find(
            (a) =>
              a.status === 'online' &&
              new Date(a.registered_at).getTime() > downloadTime - TEN_MIN
          );
          if (fresh) {
            setConnected(fresh);
            clearInterval(timer);
          }
        }
      } catch (_) { /* network error, keep polling */ }
    }, POLL_INTERVAL);

    return () => clearInterval(timer);
  }, [winDone, connected, downloadTime]);

  const MANUAL_CMD = `python -m sylink_agent run --config appsettings.json`;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-2xl border border-gray-700 w-full max-w-2xl shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-white">Télécharger l'Agent SYLink</h2>
            <p className="text-sm text-gray-400 mt-0.5">Choisissez votre système d'exploitation</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                activeTab === id
                  ? 'text-blue-400 border-b-2 border-blue-400 bg-blue-500/5'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[68vh] overflow-y-auto">

          {/* ── WINDOWS ── */}
          {activeTab === 'windows' && (
            <div className="space-y-4">

              {/* Feature badge */}
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 rounded-lg border border-blue-500/20 text-xs text-blue-300">
                <Zap className="w-3.5 h-3.5 flex-shrink-0" />
                <span><strong>Installation en 1 clic</strong> — Le ZIP est pré-configuré avec vos identifiants.</span>
              </div>

              {/* Steps */}
              <div className="space-y-2">
                {[
                  {
                    num: 1,
                    icon: Download,
                    title: 'Télécharger le ZIP',
                    desc: 'Cliquez ci-dessous. Un ZIP pré-configuré est généré instantanément avec vos identifiants.',
                    done: winDone,
                    active: !winDone,
                  },
                  {
                    num: 2,
                    icon: Shield,
                    title: 'Extraire et lancer INSTALL.cmd',
                    desc: 'Extrayez le ZIP, puis double-cliquez sur INSTALL.cmd. Acceptez l\'invite UAC.',
                    done: false,
                    active: winDone && !connected,
                  },
                  {
                    num: 3,
                    icon: CheckCircle,
                    title: 'L\'agent se connecte automatiquement',
                    desc: 'L\'installation démarre l\'agent en arrière-plan. Il apparaît dans le dashboard.',
                    done: !!connected,
                    active: winDone && !connected,
                  },
                ].map(({ num, icon: Icon, title, desc, done, active }) => (
                  <div key={num} className={`flex items-start gap-3 p-3 rounded-lg transition-all ${
                    done  ? 'bg-green-500/10 border border-green-500/20' :
                    active ? 'bg-blue-500/10 border border-blue-500/20' :
                    'bg-gray-800/40 border border-transparent'
                  }`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      done  ? 'bg-green-500/20' :
                      active ? 'bg-blue-500/20' : 'bg-gray-700/50'
                    }`}>
                      {done
                        ? <CheckCircle className="w-4 h-4 text-green-400" />
                        : <span className={`text-xs font-bold ${active ? 'text-blue-400' : 'text-gray-500'}`}>{num}</span>
                      }
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${done ? 'text-green-300' : active ? 'text-white' : 'text-gray-400'}`}>{title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Download button */}
              {!winDone && (
                <button
                  onClick={handleWindowsDownload}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl transition-all font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/20"
                >
                  <Download className="w-5 h-5" />
                  Télécharger SYLinkAgent.zip
                </button>
              )}

              {/* ── Connection Status (after download) ── */}
              {winDone && (
                <div className="space-y-3">
                  {connected ? (
                    /* Success */
                    <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/30">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                        <span className="text-sm font-semibold text-green-300">Agent connecté !</span>
                      </div>
                      <p className="text-xs text-gray-400 ml-7">
                        <span className="font-mono text-green-400">{connected.name || connected.uid}</span> est en ligne et prêt à l'emploi.
                      </p>
                    </div>
                  ) : (
                    /* Waiting */
                    <div className="p-4 bg-gray-800/60 rounded-xl border border-gray-700 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 text-blue-400 animate-spin flex-shrink-0" />
                          <span className="text-sm text-gray-300">En attente de connexion…</span>
                        </div>
                        <span className="text-xs text-gray-500 font-mono">{waitSecs}s</span>
                      </div>
                      {/* Progress bar */}
                      <div className="w-full bg-gray-700 rounded-full h-1">
                        <div
                          className="bg-blue-500 h-1 rounded-full transition-all duration-1000"
                          style={{ width: `${Math.min((waitSecs / FALLBACK_DELAY) * 100, 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Double-cliquez sur <span className="font-mono text-gray-300">INSTALL.cmd</span> et acceptez l'invite UAC.
                        L'agent se connectera automatiquement.
                      </p>

                      {/* Fallback command — shown after FALLBACK_DELAY seconds */}
                      {showFallback && (
                        <div className="pt-2 border-t border-gray-700 space-y-2">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                            <div className="text-xs space-y-0.5">
                              <p className="text-yellow-300 font-medium">L'agent ne s'est pas connecté automatiquement.</p>
                              <p className="text-gray-400">Ouvrez un terminal Administrateur <strong className="text-white">dans le dossier où vous avez extrait le ZIP</strong>, puis lancez :</p>
                            </div>
                          </div>
                          <CodeBlock code={MANUAL_CMD} />
                          <p className="text-xs text-gray-500">
                            Ou via le Gestionnaire de tâches Windows : cherchez la tâche <span className="font-mono text-gray-300">SYLinkSecurityAgent</span> et démarrez-la.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions row */}
                  <div className="flex gap-2">
                    <button
                      onClick={handleWindowsDownload}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-xs transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Retélécharger
                    </button>
                    <button
                      onClick={() => { onClose(); window.location.href = '/agents'; }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 border border-blue-500/30 rounded-lg text-xs transition-colors"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Voir les agents
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 text-xs text-gray-500">
                <Monitor className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-gray-600" />
                <span>Compatible Windows 10 / 11 / Server 2019+. Python 3.9+ requis (l'installeur guide si absent).</span>
              </div>
            </div>
          )}

          {/* ── LINUX ── */}
          {activeTab === 'linux' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                <Terminal className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Agent Linux (.deb — Debian/Ubuntu)</p>
                  <p className="text-xs text-gray-400 mt-0.5">Compatible Debian, Ubuntu, Kali et dérivés</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-300 mb-1">Installation via <span className="font-mono text-orange-300">dpkg</span> :</p>
                <CodeBlock code={`# 1. Télécharger le paquet
wget "${serverUrl}/api/v1/agents/installer/linux-deb" \\
  -O sylink-agent.deb

# 2. Installer
sudo dpkg -i sylink-agent.deb

# 3. Configurer (génère l'UID, démarre le service)
sudo sylink-agent setup`} />
              </div>

              <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-300">
                  Requiert Python 3.9+ : <span className="font-mono text-yellow-300">sudo apt install python3 python3-pip</span>
                </p>
              </div>

              <button
                onClick={() => downloadFile(`${API_BASE_URL}/agents/installer/linux-deb`, 'sylink-agent.deb')}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium"
              >
                <Download className="w-5 h-5" />
                Télécharger sylink-agent.deb
              </button>
            </div>
          )}

          {/* ── MACOS ── */}
          {activeTab === 'macos' && (
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <Apple className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Agent macOS — Script d'installation</p>
                  <p className="text-xs text-gray-400 mt-0.5">Compatible macOS 12 Monterey+ (Intel & Apple Silicon)</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-300 mb-1">Installation en une ligne :</p>
                <CodeBlock code={`curl -fsSL "${serverUrl}/api/v1/agents/installer/macos" | sudo bash`} />
              </div>

              <div>
                <p className="text-sm text-gray-300 mb-1">Ou télécharger puis vérifier :</p>
                <CodeBlock code={`curl -fsSL "${serverUrl}/api/v1/agents/installer/macos" \\
  -o install-sylink-agent.sh
sudo bash install-sylink-agent.sh
sudo sylink-agent setup`} />
              </div>

              <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
                <p className="text-xs text-gray-300">
                  <span className="text-purple-300 font-medium">Prérequis :</span> Python 3.9+
                  {' '}(<span className="font-mono text-purple-300">brew install python@3.12</span>)
                </p>
              </div>

              <button
                onClick={() => downloadFile(`${API_BASE_URL}/agents/installer/macos`, 'install-sylink-agent.sh')}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
              >
                <Download className="w-5 h-5" />
                Télécharger install-sylink-agent.sh
              </button>
            </div>
          )}

          {/* Footer note */}
          <div className="border-t border-gray-700 pt-4">
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              <span>
                L'agent Windows est <strong className="text-gray-400">pré-configuré</strong> — il n'y a rien à saisir.
                Pour Linux/macOS, notez l'<span className="font-mono text-green-400">UID</span> généré et cliquez sur "Enregistrer l'UID".
              </span>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex gap-3 p-5 border-t border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
          >
            Fermer
          </button>
          {(activeTab === 'linux' || activeTab === 'macos') && (
            <button
              onClick={() => { onClose(); onRegister(); }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
            >
              Enregistrer l'UID
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
